import { useState } from 'react';
import type { OutOfDatePublicationVariant } from './useOutOfDatePublicationVariant';

export interface UseOutOfDatePublicationActionArgs {
  variant: OutOfDatePublicationVariant | undefined;
  /** Regenerates the media. */
  onRegenerate: () => Promise<void>;
  /** Updates the live publication. */
  onUpdate: () => Promise<void>;
}

/**
 * Runs whichever mutation the current variant calls for, tracking its own
 * `isSubmitting` state. Shared by the floating widget and the compact
 * button so both spin their own button in place rather than freezing the
 * whole page.
 */
export const useOutOfDatePublicationAction = ({
  variant,
  onRegenerate,
  onUpdate,
}: UseOutOfDatePublicationActionArgs) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const runAction = async () => {
    setIsSubmitting(true);
    try {
      await (variant === 'regenerate' ? onRegenerate() : onUpdate());
    } catch {
      // Swallowed — the mutation's own error notification already surfaces
      // it; this just needs to stop the button spinning.
    } finally {
      setIsSubmitting(false);
    }
  };

  return { isSubmitting, runAction };
};
